/**
 * processes/procedures-api — katalog procedur kontrolnych per-przejście (ADR-0074,
 * `docs/ADR/0074-procedury-per-przejscie-samoobslugowe.md`), Ustawienia → „Procedury SQL" → zakładka „Procesy".
 *
 * Katalog jest TENANT-GLOBALNY (bez `company_id`) — mirror `global-dictionaries/api.ts` na
 * tenant scope. JEDNO uprawnienie `processes.procedures.manage` bramkuje wszystko (podgląd,
 * CRUD, test, restore) — patrz `useCanManageProcessProcedures`.
 *
 * Endpointy (`/api/processes/procedures`):
 *   GET    /               — lista (filtry `q` ILIKE name, `kind`)
 *   POST   /               — utwórz procedurę
 *   GET    /:id            — szczegóły
 *   PATCH  /:id            — edycja meta/treści (`kind` NIEEDYTOWALNY — backend go ignoruje)
 *   DELETE /:id            — soft-delete (409 PROCEDURE_IN_USE gdy przypięta do krawędzi)
 *   POST   /:id/restore    — undelete
 *   POST   /test-sql       — próbne wykonanie (BEZ [id] — działa też podczas tworzenia)
 *
 * `include_deleted`: `GET /api/processes/procedures?include_deleted=true` zwraca też
 * soft-skasowane wiersze (walidator + route, ADR-0074 slice 5). Hook
 * `useProcessProceduresList` wysyła `include_deleted=true` gdy `includeDeleted` (konwencja
 * CLAUDE.md §4.1). Widok skasowanych i „Przywróć" bramkuje to samo `processes.procedures.manage`
 * (katalog jednorodny — jedno uprawnienie na wszystko, bez osobnego `.view_deleted`).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

// ============================================================================
// Types
// ============================================================================

export type ProcessProcedureKind = 'validation' | 'action';

/** Mirror `DictionarySourceParam` (`lib/dictionaries/external-source-types.ts`) — kształt identyczny. */
export type ProcedureParamType = 'text' | 'int' | 'decimal' | 'date' | 'bool';

/** Wszystkie typy parametru — kolejność = kolejność opcji w edytorze listy parametrów SQL. */
export const PROCEDURE_PARAM_TYPES: readonly ProcedureParamType[] = [
  'text',
  'int',
  'decimal',
  'date',
  'bool',
] as const;

export interface ProcedureParam {
  name: string;
  type: ProcedureParamType;
  required: boolean;
}

export interface ProcessProcedure {
  id: string;
  name: string;
  description: string | null;
  kind: ProcessProcedureKind;
  sql: string;
  declaredParams: ProcedureParam[];
  createdAt?: string;
  updatedAt?: string;
  gcrecord?: string | null;
}

interface ListResponse {
  data: ProcessProcedure[];
}

interface DetailResponse {
  data: ProcessProcedure;
}

// ============================================================================
// Query keys
// ============================================================================

export const PROCESS_PROCEDURES_LIST_KEY = ['processes', 'procedures'] as const;
export const processProcedureDetailKey = (id: string) => ['processes', 'procedures', id] as const;

// ============================================================================
// Hooks — list
// ============================================================================

export interface ListProcessProceduresParams {
  q?: string;
  kind?: ProcessProcedureKind;
  /** Patrz UWAGA w nagłówku pliku — backend dziś ignoruje ten filtr. */
  includeDeleted?: boolean;
}

export function useProcessProceduresList(params: ListProcessProceduresParams = {}) {
  const { q, kind, includeDeleted } = params;
  return useQuery<ProcessProcedure[]>({
    queryKey: [
      ...PROCESS_PROCEDURES_LIST_KEY,
      { q: q ?? '', kind: kind ?? null, includeDeleted: includeDeleted ?? false },
    ] as const,
    queryFn: async () => {
      const search = new URLSearchParams();
      if (q && q.trim()) search.set('q', q.trim());
      if (kind) search.set('kind', kind);
      if (includeDeleted) search.set('include_deleted', 'true');
      const qs = search.toString();
      const res = await api.get<ListResponse>(`/processes/procedures${qs ? `?${qs}` : ''}`);
      return res.data.data;
    },
    retry: false,
  });
}

export function useProcessProcedureDetail(id: string | null) {
  return useQuery<ProcessProcedure>({
    queryKey: id ? processProcedureDetailKey(id) : ['processes', 'procedures', 'none'],
    queryFn: async () => {
      const res = await api.get<DetailResponse>(`/processes/procedures/${id}`);
      return res.data.data;
    },
    enabled: !!id,
    retry: false,
  });
}

function useInvalidateProcessProcedures() {
  const queryClient = useQueryClient();
  return (id?: string) => {
    queryClient.invalidateQueries({ queryKey: PROCESS_PROCEDURES_LIST_KEY });
    if (id) queryClient.invalidateQueries({ queryKey: processProcedureDetailKey(id) });
  };
}

// ============================================================================
// Hooks — mutations (create/update/delete/restore)
// ============================================================================

export interface CreateProcessProcedureInput {
  name: string;
  description?: string | null;
  kind: ProcessProcedureKind;
  sql: string;
  declaredParams: ProcedureParam[];
}

export function useCreateProcessProcedure() {
  const invalidate = useInvalidateProcessProcedures();
  return useMutation({
    mutationFn: async (input: CreateProcessProcedureInput) => {
      const res = await api.post<DetailResponse>('/processes/procedures', input);
      return res.data.data;
    },
    onSuccess: () => invalidate(),
  });
}

/** `kind` jest NIEEDYTOWALNY po utworzeniu — celowo brak tego pola w kształcie inputu. */
export interface UpdateProcessProcedureInput {
  name?: string;
  description?: string | null;
  sql?: string;
  declaredParams?: ProcedureParam[];
}

export function useUpdateProcessProcedure() {
  const invalidate = useInvalidateProcessProcedures();
  return useMutation({
    mutationFn: async ({ id, values }: { id: string; values: UpdateProcessProcedureInput }) => {
      const res = await api.patch<DetailResponse>(`/processes/procedures/${id}`, values);
      return res.data.data;
    },
    onSuccess: (procedure) => invalidate(procedure.id),
  });
}

export function useDeleteProcessProcedure() {
  const invalidate = useInvalidateProcessProcedures();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/processes/procedures/${id}`);
      return id;
    },
    onSuccess: (id) => invalidate(id),
  });
}

export function useRestoreProcessProcedure() {
  const invalidate = useInvalidateProcessProcedures();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/processes/procedures/${id}/restore`);
      return id;
    },
    onSuccess: (id) => invalidate(id),
  });
}

// ============================================================================
// Hooks — test-sql (próbne wykonanie, ADR-0074)
// ============================================================================

/**
 * Kontekst rezerwowy silnika dostarczany przez wołającego na potrzeby próby (mirror
 * `ProcedureTestContextSchema`, `apps/api/lib/validators/process-procedures.ts`).
 * `recordId`/`processId`/`instanceId`/`nodeId`/`userId` MUSZĄ być UUID (backend Zod
 * odrzuca inny kształt) — `ProcessProcedureFormModal` je domyślnie wypełnia placeholderami
 * (nie wymaga od usera podania 7 UUID przed pierwszym testem), z opcją nadpisania w
 * zwijanej sekcji „zaawansowane".
 */
export interface ProcedureTestContext {
  recordId: string;
  processId: string;
  instanceId: string;
  nodeId: string;
  routeKey?: string | null;
  userId: string;
  targetModule: string;
}

export interface TestProcessProcedureSqlInput {
  sql: string;
  kind: ProcessProcedureKind;
  declaredParams: ProcedureParam[];
  /** Przykładowe wartości zadeklarowanych parametrów NIE-rezerwowych (opcjonalne). */
  paramValues?: Record<string, string | number | boolean | null>;
  procContext: ProcedureTestContext;
}

export type TestProcessProcedureSqlResult =
  | { kind: 'validation'; severity: 'ok' | 'warning' | 'error'; message: string | null }
  | { kind: 'action'; rowCount: number };

interface ValidationTestResponse {
  severity: 'ok' | 'warning' | 'error';
  message: string | null;
}

interface ActionTestResponse {
  rowCount: number;
}

/**
 * Mutacja „Testuj" — `POST /api/processes/procedures/test-sql`. Odpowiedź serwera NIE niesie
 * `kind` (patrz `test-sql/route.ts`: `{severity,message}` lub `{rowCount}`) — dokładamy go
 * po stronie klienta na podstawie `input.kind` (znamy go, bo to my wysłaliśmy żądanie), żeby
 * wołający dostał wygodny discriminated union zamiast zgadywania po obecności pola.
 */
export function useTestProcessProcedureSql() {
  return useMutation({
    mutationFn: async (
      input: TestProcessProcedureSqlInput,
    ): Promise<TestProcessProcedureSqlResult> => {
      if (input.kind === 'validation') {
        const res = await api.post<ValidationTestResponse>('/processes/procedures/test-sql', input);
        return { kind: 'validation', severity: res.data.severity, message: res.data.message };
      }
      const res = await api.post<ActionTestResponse>('/processes/procedures/test-sql', input);
      return { kind: 'action', rowCount: res.data.rowCount };
    },
  });
}
